// src/pages/TeamLeadersPage.jsx

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/team-leaders-page.css';

const TeamLeadersPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="team-leaders-page">
      <section className="hero-section">
        <div className="container">
          <h1 className="gradient-title">Become an <span>Aledger Team Leader</span></h1>
          <p className="hero-subtitle">
            Build your network, generate multiple revenue streams, and help shape the future of decentralized connectivity
          </p>
          <div className="hero-cta">
            <Link to="/node" className="btn btn-primary">Apply for a Node</Link>
            <Link to="/whitepaper" className="btn btn-outline">Learn About the Economics</Link>
          </div>
        </div>
      </section>

      <section className="opportunity-overview">
        <div className="container">
          <div className="section-header">
            <h2>The Team Leader Opportunity</h2>
            <p>A once-in-a-generation chance to build a sustainable, multi-level income structure</p>
          </div>

          <div className="glass-card opportunity-card">
            <div className="opportunity-content">
              <div className="opportunity-text">
                <h3>What is an Aledger Team Leader?</h3>
                <p>
                  Aledger Team Leaders are node operators who build and manage networks of subordinate devices and nodes. 
                  By strategically positioning yourself at the top of a hierarchical structure, you can generate multiple 
                  revenue streams that grow exponentially as your network expands.
                </p>
                <p>
                  Unlike traditional referral systems, Aledger's node hierarchy creates <strong>mathematically-enforced scarcity</strong> 
                  through staking requirements, driving unprecedented token value growth while generating sustainable passive income 
                  through network stability fees from your entire downline.
                </p>

                <div className="key-benefit">
                  <h4>First Mover Advantage</h4>
                  <p>
                    Early team leaders have the opportunity to build substantial networks before token prices rise, securing 
                    positions that will become increasingly expensive to establish as the ecosystem grows.
                  </p>
                </div>
              </div>
              
              <div className="node-tiers-visual">
                <div className="node-tier-card tier-apex">
                  <h5>Apex Node</h5>
                  <p className="staking-req">1,000,000 AFI</p>
                  <ul>
                    <li>18% mining rewards</li>
                    <li>15% sales commission</li>
                    <li>Unlimited devices</li>
                    <li>Exclusive synthesis rights</li>
                  </ul>
                </div>
                <div className="node-tier-card tier-master">
                  <h5>Master Node</h5>
                  <p className="staking-req">500,000 AFI</p>
                  <ul>
                    <li>16% mining rewards</li>
                    <li>13% sales commission</li>
                    <li>Up to 600 devices</li>
                    <li>Governance voting (1.5×)</li>
                  </ul>
                </div>
                <div className="node-tier-card tier-super">
                  <h5>Super Node</h5>
                  <p className="staking-req">250,000 AFI</p>
                  <ul>
                    <li>13% mining rewards</li>
                    <li>10% sales commission</li>
                    <li>Up to 250 devices</li>
                    <li>Proposal voting (1.2×)</li>
                  </ul>
                </div>
                <div className="node-tier-card tier-standard">
                  <h5>Standard Node</h5>
                  <p className="staking-req">100,000 AFI</p>
                  <ul>
                    <li>10% mining rewards</li>
                    <li>7% sales commission</li>
                    <li>Up to 75 devices</li>
                    <li>3% superior rewards</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="revenue-streams">
        <div className="container">
          <div className="section-header">
            <h2>Multiple Revenue Streams</h2>
            <p>Team Leaders generate income through various mechanisms within the Aledger ecosystem</p>
          </div>

          <div className="revenue-streams-grid">
            <div className="revenue-card glass-card">
              <div className="revenue-icon mining-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3L17 8H14V16H10V8H7L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 17H14V21H10V17H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Mining Rewards Share</h3>
              <p>
                Team Leaders earn a percentage of all points generated by devices in their network.
                Standard nodes receive 10%, Super nodes 13%, Master nodes 16%, and Apex nodes 18%.
              </p>
              <div className="revenue-example">
                <p><strong>Example:</strong> An Apex node with 100 devices in its network, each generating 100 points daily:</p>
                <p><strong>100 devices × 100 points × 18% = 1,800 points daily</strong></p>
              </div>
            </div>

            <div className="revenue-card glass-card">
              <div className="revenue-icon stability-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Network Stability Fees</h3>
              <p>
                When your downline network generates revenue, you receive network stability fees from the entire structure, 
                creating a passive income stream that grows with your network.
              </p>
              <div className="revenue-example">
                <p><strong>Example:</strong> A node with 50 downline nodes, each earning $100 monthly:</p>
                <p><strong>50 nodes × $100 × 6% = $300 monthly stability fees</strong></p>
              </div>
            </div>

            <div className="revenue-card glass-card">
              <div className="revenue-icon sales-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 12H17L14 19L10 5L7 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Sales Commissions</h3>
              <p>
                Earn substantial commissions on device sales to your downline. Commission rates increase with your node tier: 
                7% for Standard, 10% for Super, 13% for Master, and 15% for Apex nodes.
              </p>
              <div className="revenue-example">
                <p><strong>Example:</strong> A Master node referring 10 Elite package sales at $1,999 each:</p>
                <p><strong>10 sales × $1,999 × 13% = $2,598.70 in commissions</strong></p>
              </div>
            </div>

            <div className="revenue-card glass-card">
              <div className="revenue-icon package-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="7" width="20" height="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Package Synthesis (Apex Nodes)</h3>
              <p>
                After the Genesis period, Apex nodes gain exclusive rights to create new device packages through synthesis.
                This creates a profitable arbitrage opportunity between points purchased from users and packages sold on the marketplace.
              </p>
              <div className="revenue-example">
                <p><strong>Example:</strong> Synthesizing a package using 200,000 points purchased at $0.006 per point:</p>
                <p><strong>Cost: $1,200 | Package value: $1,800 | Profit: $600 (50% ROI)</strong></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="apex-synthesis-section">
        <div className="container">
          <div className="section-header">
            <h2>Exclusive Synthesis Opportunity</h2>
            <p>How Apex Nodes create and control the device economy after Genesis</p>
          </div>

          <div className="synthesis-overview glass-card">
            <div className="synthesis-header">
              <div className="synthesis-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 16V8.00002C20.9996 7.6493 20.9071 7.30483 20.7315 7.00119C20.556 6.69754 20.3037 6.44539 20 6.27002L13 2.27002C12.696 2.09449 12.3511 2.00208 12 2.00208C11.6489 2.00208 11.304 2.09449 11 2.27002L4 6.27002C3.69626 6.44539 3.44398 6.69754 3.26846 7.00119C3.09294 7.30483 3.00036 7.6493 3 8.00002V16C3.00036 16.3508 3.09294 16.6952 3.26846 16.9989C3.44398 17.3025 3.69626 17.5547 4 17.73L11 21.73C11.304 21.9056 11.6489 21.998 12 21.998C12.3511 21.998 12.696 21.9056 13 21.73L20 17.73C20.3037 17.5547 20.556 17.3025 20.7315 16.9989C20.9071 16.6952 20.9996 16.3508 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.27002 6.96002L12 12.01L20.73 6.96002" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 22.08V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>The Post-Genesis Synthesis Economy</h3>
            </div>
            
            <div className="synthesis-description">
              <p className="synthesis-lead">
                After the initial 5,000 Genesis packages are sold, the Aledger ecosystem transitions to a revolutionary model 
                where <strong>only Apex Nodes can create new device packages</strong> through synthesis. This exclusive right 
                creates an exceptional profit opportunity unlike anything in traditional crypto.
              </p>
              
              <div className="synthesis-highlights">
                <div className="highlight-item">
                  <div className="highlight-number">01</div>
                  <div className="highlight-content">
                    <h4>Exclusive Creation Rights</h4>
                    <p>Only Apex Nodes can create new packages after Genesis, giving you direct control over the supply of mining devices in the ecosystem.</p>
                  </div>
                </div>
                
                <div className="highlight-item">
                  <div className="highlight-number">02</div>
                  <div className="highlight-content">
                    <h4>Point Acquisition Advantage</h4>
                    <p>Purchase points from users at a significant discount to their token conversion value (typically 30-40% less) for immediate synthesis.</p>
                  </div>
                </div>
                
                <div className="highlight-item">
                  <div className="highlight-number">03</div>
                  <div className="highlight-content">
                    <h4>Quality and Attribute System</h4>
                    <p>Synthesized packages have variable quality tiers and attributes that can significantly increase their market value and mining efficiency.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="synthesis-process">
              <h4>The Synthesis Process</h4>
              
              <div className="process-flow">
                <div className="process-step">
                  <div className="step-icon users-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 17L12 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 8V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="step-content">
                    <h5>Users Generate Points</h5>
                    <p>Users earn points through active devices</p>
                  </div>
                </div>
                
                <div className="flow-arrow">→</div>
                
                <div className="process-step">
                  <div className="step-icon exchange-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 1L21 5L17 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 11V9C3 7.93913 3.42143 6.92172 4.17157 6.17157C4.92172 5.42143 5.93913 5 7 5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 23L3 19L7 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 13V15C21 16.0609 20.5786 17.0783 19.8284 17.8284C19.0783 18.5786 18.0609 19 17 19H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="step-content">
                    <h5>Apex Nodes Buy Points</h5>
                    <p>Purchase at discount to token value</p>
                  </div>
                </div>
                
                <div className="flow-arrow">→</div>
                
                <div className="process-step">
                  <div className="step-icon synthesis-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="step-content">
                    <h5>Synthesis Occurs</h5>
                    <p>Points converted to device packages</p>
                  </div>
                </div>
                
                <div className="flow-arrow">→</div>
                
                <div className="process-step">
                  <div className="step-icon market-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 7H7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="step-content">
                    <h5>Market Sale</h5>
                    <p>Packages sold for significant profit</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="synthesis-economics">
              <h4>Synthesis Economics</h4>
              
              <div className="economics-container">
                <div className="economics-calculation">
                  <div className="calculation-header">
                    <h5>Profit Example</h5>
                    <p>Creating a new device package through synthesis</p>
                  </div>
                  
                  <div className="calculation-details">
                    <div className="calculation-row">
                      <div className="calc-label">Point Acquisition</div>
                      <div className="calc-formula">200,000 points × $0.006</div>
                      <div className="calc-result">$1,200</div>
                    </div>
                    
                    <div className="calculation-row">
                      <div className="calc-label">Package Value</div>
                      <div className="calc-formula">Market price</div>
                      <div className="calc-result">$1,800</div>
                    </div>
                    
                    <div className="calculation-divider"></div>
                    
                    <div className="calculation-row total">
                      <div className="calc-label">Net Profit</div>
                      <div className="calc-formula">$1,800 - $1,200</div>
                      <div className="calc-result profit">$600 (50% ROI)</div>
                    </div>
                  </div>
                </div>
                
                <div className="economics-explanation">
                  <h5>Why This Creates Significant Profit</h5>
                  <ul className="profit-points">
                    <li>
                      <span className="point-marker"></span>
                      <span className="point-text">Users prefer immediate liquidity over waiting 3-5 days for point-to-token conversion</span>
                    </li>
                    <li>
                      <span className="point-marker"></span>
                      <span className="point-text">The arbitrage window creates a natural 30-50% profit margin on every synthesis</span>
                    </li>
                    <li>
                      <span className="point-marker"></span>
                      <span className="point-text">Higher quality tiers (Premium, Epic, Legendary) can yield even greater returns</span>
                    </li>
                    <li>
                      <span className="point-marker"></span>
                      <span className="point-text">As demand grows, the profit margin naturally expands due to supply limitations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="quality-tiers">
              <h4>Synthesis Quality Tiers</h4>
              
              <div className="tiers-grid">
                <div className="tier-card standard-tier">
                  <div className="tier-header">
                    <h5>Standard</h5>
                    <span className="tier-chance">60% chance</span>
                  </div>
                  <div className="tier-details">
                    <div className="tier-stat">
                      <span className="stat-name">Mining Efficiency</span>
                      <span className="stat-value">Base (100%)</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Attribute Sockets</span>
                      <span className="stat-value">3</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Value Multiplier</span>
                      <span className="stat-value">1.0×</span>
                    </div>
                  </div>
                </div>
                
                <div className="tier-card premium-tier">
                  <div className="tier-header">
                    <h5>Premium</h5>
                    <span className="tier-chance">30% chance</span>
                  </div>
                  <div className="tier-details">
                    <div className="tier-stat">
                      <span className="stat-name">Mining Efficiency</span>
                      <span className="stat-value">+15%</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Attribute Sockets</span>
                      <span className="stat-value">4</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Value Multiplier</span>
                      <span className="stat-value">1.3×</span>
                    </div>
                  </div>
                </div>
                
                <div className="tier-card epic-tier">
                  <div className="tier-header">
                    <h5>Epic</h5>
                    <span className="tier-chance">8% chance</span>
                  </div>
                  <div className="tier-details">
                    <div className="tier-stat">
                      <span className="stat-name">Mining Efficiency</span>
                      <span className="stat-value">+30%</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Attribute Sockets</span>
                      <span className="stat-value">5</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Value Multiplier</span>
                      <span className="stat-value">1.7×</span>
                    </div>
                  </div>
                </div>
                
                <div className="tier-card legendary-tier">
                  <div className="tier-header">
                    <h5>Legendary</h5>
                    <span className="tier-chance">2% chance</span>
                  </div>
                  <div className="tier-details">
                    <div className="tier-stat">
                      <span className="stat-name">Mining Efficiency</span>
                      <span className="stat-value">+50%</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Attribute Sockets</span>
                      <span className="stat-value">5 + 1 rare</span>
                    </div>
                    <div className="tier-stat">
                      <span className="stat-name">Value Multiplier</span>
                      <span className="stat-value">2.5×</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="apex-advantage">
              <h4>The Apex Node Synthesis Advantage</h4>
              <p class="advantage-description">
                As an Apex Node operator with exclusive synthesis rights, you gain unprecedented control 
                over the supply of new devices in the ecosystem. This creates a powerful position where you 
                can dictate market dynamics and capture significant profits through the arbitrage between 
                point costs and package values.
              </p>
              
              <div className="advantage-quote">
                "Package synthesis is not just another revenue stream - it's the control mechanism for the 
                entire post-Genesis economy. Apex Node operators who master this process will generate 
                extraordinary returns while shaping the future of the ecosystem."
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="token-growth">
        <div className="container">
          <div className="section-header">
            <h2>Token Value Growth Potential</h2>
            <p>How hardware staking creates unprecedented appreciation</p>
          </div>

          <div className="glass-card token-growth-card">
            <h3>Mathematically Inevitable Value Increase</h3>
            <p>
              The Aledger model creates a fundamental mathematical certainty: as more devices join the network, 
              more tokens must be staked, removing them from circulation. Since the total supply is fixed at 1 billion AFI, 
              this creates a supply-demand imbalance that can only be resolved through price appreciation.
            </p>
            
            <div className="math-explanation">
              <div className="math-section">
                <h4>The Supply-Demand Equation</h4>
                <p>
                  When network growth reaches just 50,000 devices, the staking demand will be approximately 3 billion AFI tokens - 
                  300% of the total supply. By 100,000 devices, demand reaches 600% of supply.
                </p>
                <p>
                  <strong>This is not speculation - it's mathematical certainty.</strong> The only way to resolve this imbalance is 
                  through significant price appreciation, as users compete for the limited token supply.
                </p>
              </div>
              
              <div className="projection-chart">
                <h4>Token Price Projection Timeline</h4>
                <div className="chart-bars">
                  <div className="chart-bar">
                    <div className="bar-fill" style={{ height: '5%' }}></div>
                    <div className="bar-label">Initial</div>
                    <div className="price-label">$0.02</div>
                  </div>
                  <div className="chart-bar">
                    <div className="bar-fill" style={{ height: '20%' }}></div>
                    <div className="bar-label">5,000 Devices</div>
                    <div className="price-label">$0.30</div>
                  </div>
                  <div className="chart-bar">
                    <div className="bar-fill" style={{ height: '40%' }}></div>
                    <div className="bar-label">25,000 Devices</div>
                    <div className="price-label">$2.00</div>
                  </div>
                  <div className="chart-bar">
                    <div className="bar-fill" style={{ height: '75%' }}></div>
                    <div className="bar-label">50,000 Devices</div>
                    <div className="price-label">$5.00</div>
                  </div>
                  <div className="chart-bar">
                    <div className="bar-fill" style={{ height: '100%' }}></div>
                    <div className="bar-label">100,000 Devices</div>
                    <div className="price-label">$15.00</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="network-growth">
        <div className="container">
          <div className="section-header">
            <h2>Network Growth Strategy</h2>
            <p>Building your downline for maximum returns</p>
          </div>

          <div className="strategy-steps">
            <div className="strategy-step glass-card">
              <div className="step-number">1</div>
              <h3>Establish Your Node Position</h3>
              <p>
                Apply for your node and stake the required AFI tokens. The higher your node tier, the greater your earning potential. 
                Secure your position early while token prices are low.
              </p>
            </div>
            
            <div className="strategy-step glass-card">
              <div className="step-number">2</div>
              <h3>Recruit Direct Subordinates</h3>
              <p>
                Use your invitation code to recruit direct subordinates to your network. Focus on quality recruits who understand 
                the vision and are committed to building their own networks.
              </p>
            </div>
            
            <div className="strategy-step glass-card">
              <div className="step-number">3</div>
              <h3>Help Subordinates Build Their Networks</h3>
              <p>
                Support your direct subordinates in building their own networks. As their downlines grow, you earn network stability 
                fees from the entire structure, creating exponential growth potential.
              </p>
            </div>
            
            <div className="strategy-step glass-card">
              <div className="step-number">4</div>
              <h3>Upgrade Your Node Tier</h3>
              <p>
                As your returns increase, consider upgrading to a higher node tier to maximize your earnings potential. 
                Apex nodes have unlimited device capacity and exclusive synthesis rights.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="income-calculator">
        <div className="container">
          <div className="section-header">
            <h2>Potential Income Scenarios</h2>
            <p>Examples of what's possible as your network grows</p>
          </div>

          <div className="glass-card calculator-card">
            <h3>Income Projection Examples</h3>
            
            <div className="income-scenarios">
              <div className="scenario">
                <h4>Small Network</h4>
                <p><strong>Network Size:</strong> 20 direct subordinates, 100 total network size</p>
                <p><strong>Node Level:</strong> Standard Node</p>
                <div className="income-breakdown">
                  <div className="income-item">
                    <span>Mining Rewards (10%):</span>
                    <span>~$500/month</span>
                  </div>
                  <div className="income-item">
                    <span>Network Stability Fees:</span>
                    <span>~$300/month</span>
                  </div>
                  <div className="income-item">
                    <span>Sales Commissions (avg):</span>
                    <span>~$350/month</span>
                  </div>
                  <div className="income-item total">
                    <span>Total Monthly Income:</span>
                    <span>~$1,150/month</span>
                  </div>
                </div>
              </div>
              
              <div className="scenario">
                <h4>Medium Network</h4>
                <p><strong>Network Size:</strong> 50 direct subordinates, 500 total network size</p>
                <p><strong>Node Level:</strong> Super Node</p>
                <div className="income-breakdown">
                  <div className="income-item">
                    <span>Mining Rewards (13%):</span>
                    <span>~$3,250/month</span>
                  </div>
                  <div className="income-item">
                    <span>Network Stability Fees:</span>
                    <span>~$1,500/month</span>
                  </div>
                  <div className="income-item">
                    <span>Sales Commissions (avg):</span>
                    <span>~$1,000/month</span>
                  </div>
                  <div className="income-item total">
                    <span>Total Monthly Income:</span>
                    <span>~$5,750/month</span>
                  </div>
                </div>
              </div>
              
              <div className="scenario">
                <h4>Large Network</h4>
                <p><strong>Network Size:</strong> 100 direct subordinates, 2,000 total network size</p>
                <p><strong>Node Level:</strong> Apex Node</p>
                <div className="income-breakdown">
                  <div className="income-item">
                    <span>Mining Rewards (18%):</span>
                    <span>~$18,000/month</span>
                  </div>
                  <div className="income-item">
                    <span>Network Stability Fees:</span>
                    <span>~$6,000/month</span>
                  </div>
                  <div className="income-item">
                    <span>Sales Commissions (avg):</span>
                    <span>~$3,000/month</span>
                  </div>
                  <div className="income-item">
                    <span>Package Synthesis (exclusive):</span>
                    <span>~$5,000/month</span>
                  </div>
                  <div className="income-item total">
                    <span>Total Monthly Income:</span>
                    <span>~$32,000/month</span>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="calculator-disclaimer">
              These scenarios are examples based on theoretical network sizes and activity levels. Actual results may vary based on 
              network performance, token values, and individual effort. No specific income is guaranteed.
            </p>
          </div>
        </div>
      </section>

      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>Team Leader Success Stories</h2>
            <p>Hear from early adopters who are building their networks</p>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card glass-card">
              <div className="testimonial-content">
                <p>
                  "I started with a Standard Node just three months ago, and I've already built a network of 35 downline nodes. 
                  The hardware staking model creates real scarcity, and I've seen my token value appreciate significantly while 
                  my network generates consistent passive income."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar"></div>
                <div className="author-info">
                  <h4>Michael T.</h4>
                  <p>Super Node Operator</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card glass-card">
              <div className="testimonial-content">
                <p>
                  "The node hierarchy system is genius. As a Master Node operator, I earn from my entire downline network. 
                  What I love most is how the system's mathematics ensures the token value keeps growing - it's not just 
                  speculation but a mathematical certainty as more devices join."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar"></div>
                <div className="author-info">
                  <h4>Sarah K.</h4>
                  <p>Master Node Operator</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card glass-card">
              <div className="testimonial-content">
                <p>
                  "I upgraded to an Apex Node early, which was one of the best decisions I've made. The synthesis rights alone 
                  generate significant profit, and my network has grown to over 500 devices. The multi-stream income model is 
                  unlike anything I've seen in crypto."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar"></div>
                <div className="author-info">
                  <h4>David L.</h4>
                  <p>Apex Node Operator</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Common questions about becoming an Aledger Team Leader</p>
          </div>

          <div className="faq-container">
            <div className="faq-item glass-card">
              <div className="faq-question">
                <h3>How much can I earn as a Team Leader?</h3>
                <span className="toggle-icon">+</span>
              </div>
              <div className="faq-answer">
                <p>
                  Earnings vary based on your node tier, network size, and activity levels. Team Leaders earn through multiple 
                  revenue streams: mining rewards (10-18%), network stability fees, sales commissions (7-15%), and for Apex nodes, 
                  package synthesis profits. With a well-developed network, monthly earnings can range from a few hundred dollars 
                  to tens of thousands.
                </p>
              </div>
            </div>
            
            <div className="faq-item glass-card">
              <div className="faq-question">
                <h3>What's the minimum investment to become a Team Leader?</h3>
                <span className="toggle-icon">+</span>
              </div>
              <div className="faq-answer">
                <p>
                  To become a Team Leader, you need to apply for a node and stake the required AFI tokens. A Standard Node requires 
                  staking 100,000 AFI tokens. At the current price of $0.02 per token, that's approximately $2,000. The investment 
                  increases with higher node tiers: Super Node (250,000 AFI), Master Node (500,000 AFI), and Apex Node (1,000,000 AFI).
                </p>
              </div>
            </div>
            
            <div className="faq-item glass-card">
              <div className="faq-question">
                <h3>How does the token value grow over time?</h3>
                <span className="toggle-icon">+</span>
              </div>
              <div className="faq-answer">
                <p>
                  The token value grows through the hardware-enforced staking mechanism. Each device requires substantial token staking, 
                  removing tokens from circulation. With a fixed supply of 1 billion AFI tokens, as more devices join the network, 
                  staking demand exceeds the total supply, creating a mathematical scarcity that drives price appreciation. This is not 
                  speculation but a mathematical certainty based on network growth.
                </p>
              </div>
            </div>
            
            <div className="faq-item glass-card">
              <div className="faq-question">
                <h3>What advantages do higher node tiers have?</h3>
                <span className="toggle-icon">+</span>
              </div>
              <div className="faq-answer">
                <p>
                  Higher node tiers offer several advantages:
                </p>
                <ul>
                  <li>Higher mining reward percentages (from 10% to 18%)</li>
                  <li>Increased sales commissions (from 7% to 15%)</li>
                  <li>Greater device management capacity</li>
                  <li>Enhanced governance voting power</li>
                  <li>Apex nodes exclusively receive package synthesis rights after the Genesis period</li>
                </ul>
                <p>
                  These benefits compound to create significantly higher earning potential for upper-tier nodes.
                </p>
              </div>
            </div>
            
            <div className="faq-item glass-card">
              <div className="faq-question">
                <h3>How soon can I start earning as a Team Leader?</h3>
                <span className="toggle-icon">+</span>
              </div>
              <div className="faq-answer">
                <p>
                  You can start earning immediately after your node is approved and you begin building your network. Mining rewards and 
                  network stability fees are generated daily, while sales commissions are earned as your referrals purchase devices. 
                  The key to maximizing earnings is to actively build and support your network, especially in the early stages.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Become an Aledger Team Leader?</h2>
          <p>
            Take the first step toward building your network and generating multiple streams of passive income 
            while being part of a revolutionary ecosystem.
          </p>
          <div className="cta-buttons">
            <Link to="/node" className="btn btn-primary">Apply for a Node</Link>
            <Link to="/referrals" className="btn btn-outline">Manage Referrals</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamLeadersPage;
